import React from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import { InputBase, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { FONT_STACK_SUISSE_WORKS } from '~/components/brand';

export type TitleFieldProps = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  /** 'field': Format, Layout's labelled control, unchanged from before this
   *  extraction. 'plain': the card strip's inline look, plain text until
   *  focused, then an underline hairline with no boxed input. */
  variant?: 'field' | 'plain';
};

/**
 * The one control that writes a card's title. Format, Layout and the card
 * strip both render this component against the same draft value and the
 * same onChange, so a card has exactly one piece of title state with two
 * places to reach it, not two independently-typed text fields.
 */
export default function TitleField({
  autoFocus,
  disabled,
  inputRef,
  onChange,
  value,
  variant = 'field',
}: TitleFieldProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);

  if (variant === 'plain') {
    return (
      <InputBase
        autoFocus={autoFocus}
        disabled={disabled}
        fullWidth
        inputRef={inputRef}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Untitled card"
        slotProps={{ input: { 'aria-label': 'Card title' } }}
        value={value}
        sx={{
          flex: 1,
          fontFamily: FONT_STACK_SUISSE_WORKS,
          fontSize: 18,
          minWidth: 0,
          '& .MuiInputBase-input': {
            borderBottom: '1px solid transparent',
            overflow: 'hidden',
            padding: 0,
            textOverflow: 'ellipsis',
            transition: theme.transitions.create(['border-color']),
            whiteSpace: 'nowrap',
          },
          '&:hover .MuiInputBase-input': { borderBottomColor: 'divider' },
          '&.Mui-focused .MuiInputBase-input': { borderBottomColor: tokens.accent },
        }}
      />
    );
  }

  return (
    <TextField
      autoFocus={autoFocus}
      disabled={disabled}
      fullWidth
      inputRef={inputRef}
      label="Card title"
      onChange={(event) => onChange(event.target.value)}
      size="small"
      value={value}
    />
  );
}
